import { useEffect, useMemo, useState } from "react";
import { onAuthStateChanged, signInWithPopup, signOut } from "firebase/auth";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { auth, googleProvider, hasFirebaseConfig } from "../firebaseClient";
import {
  addGroceryItem,
  getGroceryItems,
  getGroceryItemsCount,
  isCloudBacked,
  updateGroceryItem,
} from "../services/groceryStore";
import "./Grocery.css";

const initialFormState = {
  name: "",
  price: "",
  image: "",
};

const scoreRecentItemAgainstQuery = (item, query) => {
  if (!query) {
    return 1;
  }

  const name = String(item?.name || "").toLowerCase();
  if (!name) {
    return 0;
  }

  if (name === query) {
    return 120;
  }

  if (name.startsWith(query)) {
    return 100 - (name.length - query.length) * 0.2;
  }

  const containsIndex = name.indexOf(query);
  if (containsIndex >= 0) {
    return 80 - containsIndex;
  }

  let qIdx = 0;
  for (let i = 0; i < name.length && qIdx < query.length; i += 1) {
    if (name[i] === query[qIdx]) {
      qIdx += 1;
    }
  }

  if (qIdx === query.length) {
    return 50;
  }

  return 0;
};

function GroceryAuthPanel({
  authFeedback,
  handleGoogleLogin,
  handleLogout,
  isSigningIn,
  isWhitelisted,
  user,
}) {
  return (
    <section className="grocery-auth-inline" aria-live="polite">
      {user ? (
        <>
          <p className="grocery-auth-email">
            Signed in as <strong>{user.email}</strong>
          </p>
          {isWhitelisted ? (
            <p className="grocery-auth-ok">
              Whitelisted account. You can add items.
            </p>
          ) : (
            <p className="grocery-auth-warning">
              This account is not whitelisted. Add it to
              REACT_APP_ALLOWED_EMAILS.
            </p>
          )}
          <button
            type="button"
            className="grocery-link-subtle"
            onClick={handleLogout}
          >
            Logout
          </button>
        </>
      ) : (
        <>
          <p className="grocery-auth-prompt">
            Sign in with Google to add items.
          </p>
          <button
            type="button"
            className="grocery-link-button grocery-auth-cta"
            onClick={handleGoogleLogin}
            disabled={isSigningIn}
          >
            {isSigningIn ? "Signing in..." : "Sign in with Google"}
          </button>
        </>
      )}

      {authFeedback ? (
        <p className="grocery-auth-feedback">{authFeedback}</p>
      ) : null}
    </section>
  );
}

GroceryAuthPanel.propTypes = {
  authFeedback: PropTypes.string.isRequired,
  handleGoogleLogin: PropTypes.func.isRequired,
  handleLogout: PropTypes.func.isRequired,
  isSigningIn: PropTypes.bool.isRequired,
  isWhitelisted: PropTypes.bool.isRequired,
  user: PropTypes.shape({
    email: PropTypes.string,
  }),
};

GroceryAuthPanel.defaultProps = {
  user: null,
};

function GroceryAddItemsPage() {
  const [form, setForm] = useState(initialFormState);
  const [isSaving, setIsSaving] = useState(false);
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [toast, setToast] = useState(null);
  const [imagePreview, setImagePreview] = useState({
    status: "idle",
    url: "",
    error: "",
  });
  const [authFeedback, setAuthFeedback] = useState("");
  const [user, setUser] = useState(null);
  const [allItems, setAllItems] = useState([]);
  const [recentItems, setRecentItems] = useState([]);
  const [totalItemsCount, setTotalItemsCount] = useState(0);
  const [recentSearchInput, setRecentSearchInput] = useState("");
  const [recentSearchQuery, setRecentSearchQuery] = useState("");
  const [editingItem, setEditingItem] = useState(null);

  const allowedEmails = String(process.env.REACT_APP_ALLOWED_EMAILS || "")
    .split(",")
    .map((entry) => entry.trim().toLowerCase())
    .filter(Boolean);

  const userEmail = (user?.email || "").toLowerCase();
  const isWhitelisted =
    allowedEmails.length === 0 || allowedEmails.includes(userEmail);
  const canAddItems = !hasFirebaseConfig || (Boolean(user) && isWhitelisted);

  const loadRecentItems = async () => {
    const [items, totalCount] = await Promise.all([
      getGroceryItems(),
      getGroceryItemsCount(),
    ]);
    setAllItems(items);
    setRecentItems(items.slice(0, 8));
    setTotalItemsCount(totalCount);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setRecentSearchQuery(recentSearchInput.trim().toLowerCase());
    }, 120);

    return () => clearTimeout(timer);
  }, [recentSearchInput]);

  const filteredRecentItems = useMemo(() => {
    if (!recentSearchQuery) {
      return recentItems;
    }

    return allItems
      .map((item) => ({
        item,
        score: scoreRecentItemAgainstQuery(item, recentSearchQuery),
      }))
      .filter((entry) => entry.score > 0)
      .sort((left, right) => {
        if (right.score !== left.score) {
          return right.score - left.score;
        }

        return String(left.item.name || "").localeCompare(
          String(right.item.name || ""),
        );
      })
      .map((entry) => entry.item);
  }, [allItems, recentItems, recentSearchQuery]);

  useEffect(() => {
    loadRecentItems();
  }, []);

  useEffect(() => {
    if (!toast) {
      return undefined;
    }

    const timer = setTimeout(() => {
      setToast(null);
    }, 2600);

    return () => clearTimeout(timer);
  }, [toast]);

  useEffect(() => {
    const imageUrl = form.image.trim();

    if (!imageUrl) {
      setImagePreview({ status: "idle", url: "", error: "" });
      return undefined;
    }

    setImagePreview({ status: "loading", url: "", error: "" });

    const probe = new Image();
    let disposed = false;

    probe.onload = () => {
      if (disposed) {
        return;
      }

      setImagePreview({ status: "ready", url: imageUrl, error: "" });
    };

    probe.onerror = () => {
      if (disposed) {
        return;
      }

      setImagePreview({
        status: "error",
        url: "",
        error: "Unable to load this image URL.",
      });
    };

    probe.src = imageUrl;

    return () => {
      disposed = true;
    };
  }, [form.image]);

  useEffect(() => {
    if (!hasFirebaseConfig || !auth) {
      return undefined;
    }

    const unsubscribe = onAuthStateChanged(auth, (nextUser) => {
      setUser(nextUser);
    });

    return unsubscribe;
  }, []);

  const updateField = (key, value) => {
    setForm((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const handleEditItem = (item) => {
    setEditingItem(item);
    setForm({
      name: item.name,
      price: String(item.price),
      image: item.image || "",
    });
    setFeedback("");
    setToast(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleCancelEdit = () => {
    setEditingItem(null);
    setForm(initialFormState);
    setFeedback("");
    setImagePreview({ status: "idle", url: "", error: "" });
  };

  const saveUpdate = async (name, price, image) => {
    console.info("[grocery:update-item] updating item", {
      id: editingItem.id,
      name,
      price,
      hasImage: Boolean(image),
      mode: isCloudBacked() ? "firebase" : "local",
    });
    await updateGroceryItem(editingItem.id, { name, price, image });
    console.info("[grocery:update-item] updated successfully", { name });
    setToast({ type: "success", message: `Updated ${name} successfully.` });
    setFeedback("Item updated successfully.");
    setEditingItem(null);
  };

  const saveNew = async (name, quantity, price, image) => {
    console.info("[grocery:add-item] storing item", {
      name,
      quantity,
      price,
      hasImage: Boolean(image),
      mode: isCloudBacked() ? "firebase" : "local",
    });
    await addGroceryItem({ name, quantity, price, image });
    console.info("[grocery:add-item] stored item successfully", { name });
    setFeedback("Item added successfully.");
    setToast({ type: "success", message: `Stored ${name} successfully.` });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFeedback("");
    setToast(null);

    if (!canAddItems) {
      setFeedback("Sign in with an allowed Google account to add items.");
      return;
    }

    const name = form.name.trim();
    const quantity = 1;
    const price = Number(form.price);
    const image = form.image.trim();

    if (!name) {
      setFeedback("Item name is required.");
      return;
    }

    if (!Number.isFinite(price) || price < 0) {
      setFeedback("Price must be 0 or more.");
      return;
    }

    try {
      setIsSaving(true);

      if (editingItem) {
        await saveUpdate(name, price, image);
      } else {
        await saveNew(name, quantity, price, image);
      }

      setForm(initialFormState);
      setImagePreview({ status: "idle", url: "", error: "" });
      await loadRecentItems();
    } catch (error) {
      const details = error instanceof Error ? ` (${error.message})` : "";
      console.error(
        editingItem
          ? "[grocery:update-item] failed to update item"
          : "[grocery:add-item] failed to store item",
        { name, error },
      );
      setFeedback(
        `Failed to ${editingItem ? "update" : "save"} item. Check Firebase config or try again.${details}`,
      );
      setToast({
        type: "error",
        message: `Failed to ${editingItem ? "update" : "store"} item.`,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleGoogleLogin = async () => {
    if (!auth || !googleProvider) {
      setAuthFeedback("Firebase auth is not configured.");
      return;
    }

    setAuthFeedback("");
    try {
      setIsSigningIn(true);
      await signInWithPopup(auth, googleProvider);
    } catch (error) {
      const details = error instanceof Error ? ` (${error.message})` : "";
      setAuthFeedback(`Google sign-in failed.${details}`);
    } finally {
      setIsSigningIn(false);
    }
  };

  const handleLogout = async () => {
    if (!auth) {
      return;
    }

    setAuthFeedback("");
    await signOut(auth);
  };

  let submitLabel = "Add item";
  if (isSaving) {
    submitLabel = "Saving...";
  } else if (editingItem) {
    submitLabel = "Update item";
  }

  return (
    <main className="grocery-page">
      <section className="grocery-hero">
        <div className="grocery-hero-top">
          <div className="grocery-hero-copy">
            <p className="grocery-kicker">Inventory Admin</p>
            <h1>Add grocery items</h1>
            <p>
              Add items from /grocery/add-items. Persistence mode:{" "}
              {isCloudBacked() ? "Firebase" : "Local storage fallback"}.
            </p>
            <div className="grocery-actions">
              <Link className="grocery-link-button" to="/grocery">
                Open Grocery Search
              </Link>
              <Link className="grocery-link-subtle" to="/grocery/stats">
                View Stats
              </Link>
              <Link className="grocery-link-subtle" to="/">
                Back to Home
              </Link>
            </div>
          </div>

          {hasFirebaseConfig ? (
            <GroceryAuthPanel
              authFeedback={authFeedback}
              handleGoogleLogin={handleGoogleLogin}
              handleLogout={handleLogout}
              isSigningIn={isSigningIn}
              isWhitelisted={isWhitelisted}
              user={user}
            />
          ) : null}
        </div>
      </section>

      <section className="grocery-layout grocery-layout-single">
        {toast ? (
          <output className={`grocery-toast grocery-toast-${toast.type}`}>
            {toast.message}
          </output>
        ) : null}

        <form
          className={`grocery-form ${canAddItems ? "" : "grocery-form-locked"}`.trim()}
          onSubmit={handleSubmit}
        >
          <div className="grocery-image-hero" aria-live="polite">
            {imagePreview.status === "idle" ? (
              <p className="grocery-image-hero-placeholder">
                Paste an image URL to preview your item hero.
              </p>
            ) : null}

            {imagePreview.status === "loading" ? (
              <p className="grocery-image-hero-loading">
                Fetching image preview...
              </p>
            ) : null}

            {imagePreview.status === "ready" ? (
              <img src={imagePreview.url} alt="Item preview" />
            ) : null}

            {imagePreview.status === "error" ? (
              <p className="grocery-image-hero-error">{imagePreview.error}</p>
            ) : null}
          </div>

          <label htmlFor="item-name">Item name</label>
          <input
            id="item-name"
            value={form.name}
            onChange={(event) => updateField("name", event.target.value)}
            placeholder="Ex: Fuji Apple"
            disabled={!canAddItems || isSaving}
          />

          <label htmlFor="item-price">Price</label>
          <input
            id="item-price"
            type="number"
            min="0"
            step="0.01"
            value={form.price}
            onChange={(event) => updateField("price", event.target.value)}
            placeholder="Ex: PHP 50"
            disabled={!canAddItems || isSaving}
          />

          <label htmlFor="item-image">Image URL</label>
          <input
            id="item-image"
            type="url"
            value={form.image}
            onChange={(event) => updateField("image", event.target.value)}
            placeholder="https://..."
            disabled={!canAddItems || isSaving}
          />

          <button type="submit" disabled={isSaving || !canAddItems}>
            {submitLabel}
          </button>

          {editingItem ? (
            <button
              type="button"
              className="grocery-cancel-edit-btn"
              onClick={handleCancelEdit}
              disabled={isSaving}
            >
              Cancel edit
            </button>
          ) : null}

          {feedback ? <p className="grocery-message">{feedback}</p> : null}
        </form>

        <aside className="grocery-cart">
          <h2>
            {editingItem
              ? `Editing item · Recently added (${totalItemsCount})`
              : `Recently added (${totalItemsCount})`}
          </h2>
          <input
            type="search"
            className="grocery-search grocery-recent-search"
            value={recentSearchInput}
            onChange={(event) => setRecentSearchInput(event.target.value)}
            placeholder="Search items to edit"
          />
          {filteredRecentItems.length === 0 ? <p>No items found.</p> : null}
          <div className="grocery-recent-list">
            {filteredRecentItems.map((item) => (
              <button
                type="button"
                className={`grocery-recent-card${
                  editingItem?.id === item.id
                    ? " grocery-recent-card-active"
                    : ""
                }`}
                key={item.id}
                onClick={() => handleEditItem(item)}
                title="Click to edit"
              >
                <div className="grocery-recent-thumb">
                  {item.image ? (
                    <img src={item.image} alt={item.name} />
                  ) : (
                    <span className="grocery-recent-thumb-placeholder">?</span>
                  )}
                </div>
                <div className="grocery-recent-info">
                  <p className="grocery-recent-name">{item.name}</p>
                  <p className="grocery-recent-price">
                    ₱{item.price.toFixed(2)}
                  </p>
                </div>
                <span className="grocery-recent-edit-icon" aria-hidden="true">
                  &#9998;
                </span>
              </button>
            ))}
          </div>
        </aside>
      </section>
    </main>
  );
}

export default GroceryAddItemsPage;
